import { useState, useEffect } from 'react'
import { X, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, CreditCard } from 'lucide-react'
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

interface Pagamento {
    id: string
    txid: string
    end_to_end_id: string | null
    valor: number
    status: string
    data_vencimento: string
    data_pagamento: string | null
    metodo_pagamento: string
    tentativa_numero: number
    plano_nome: string
    created_at: string
}

interface DetalhesAssinaturaModalProps {
    isOpen: boolean
    onClose: () => void
    assinaturaId: string
    userId: string
    corretorNome: string
    corretorEmail: string
    planoNome: string
    planoPreco: number
    status: string
    dataInicio: string
    dataFim: string | null
    dataProximoPagamento: string | null
    asaasAssinatura: {
        id: string
        asaas_subscription_id: string
        status: string
        cycle: string
        billing_type: string
        valor: number
    } | null
    recorrencia: any | null
    observacoes?: string | null
}

export function DetalhesAssinaturaModal({
    isOpen,
    onClose,
    assinaturaId,
    userId,
    corretorNome,
    corretorEmail,
    planoNome,
    planoPreco,
    status,
    dataInicio,
    dataFim,
    dataProximoPagamento,
    asaasAssinatura,
    recorrencia,
    observacoes
}: DetalhesAssinaturaModalProps) {
    const [pagamentos, setPagamentos] = useState<Pagamento[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (isOpen && userId) {
            carregarPagamentos()
        }
    }, [isOpen, userId, assinaturaId, recorrencia?.id_rec_efi, status])

    const recorrenciaConfirmada = (rec: any | null | undefined) => {
        if (!rec) return false
        const st = String(rec.status || '').toLowerCase()
        return ['aprovada', 'ativa', 'criada'].includes(st) || Boolean(rec.id_rec_efi)
    }

    const marcarPagamentosComoPago = async (lista: Pagamento[]) => {
        const idRec = recorrencia?.id_rec_efi as string | undefined
        const dataPagamento = new Date().toISOString()
        const deveReconciliar =
            status === 'ativa' &&
            (recorrenciaConfirmada(recorrencia) || Boolean(assinaturaId))

        if (!deveReconciliar) return lista

        const pendentes = lista.filter((p) => {
            const st = (p.status || '').toLowerCase()
            if (st !== 'pendente' && st !== 'pending') return false
            if ((p.metodo_pagamento || '').toLowerCase() !== 'pix') return false

            if (idRec && (p.txid === idRec || String(p.txid || '').includes(idRec))) {
                return true
            }

            // Só reconcilia cobranças já vencidas / do ciclo atual (não a próxima mensalidade futura)
            const venc = extractDateOnly(p.data_vencimento)
            if (!venc) return Number(p.valor) === Number(planoPreco)
            const hoje = new Date()
            const limite = new Date(hoje)
            limite.setDate(limite.getDate() + 2)
            const vencDate = new Date(`${venc}T00:00:00`)
            return Number(p.valor) === Number(planoPreco) && vencDate <= limite
        })

        for (const pagamento of pendentes) {
            try {
                await supabase.rpc('atualizar_status_pagamento', {
                    p_txid: pagamento.txid,
                    p_status: 'pago',
                    p_end_to_end_id: null,
                    p_data_pagamento: dataPagamento,
                    p_detalhes_pix: {
                        provider: 'efi',
                        origem: 'reconciliacao_admin_detalhes',
                        idRec: idRec || null,
                        assinaturaId
                    }
                })
            } catch (err) {
                console.warn('Falha ao reconciliar pagamento via RPC:', pagamento.txid, err)
            }
        }

        // Também tenta update direto (pode falhar por RLS, mas ajuda em ambientes liberados)
        if (assinaturaId) {
            await supabase
                .from('pagamentos')
                .update({
                    status: 'pago',
                    data_pagamento: dataPagamento,
                    detalhes_pix: {
                        provider: 'efi',
                        origem: 'reconciliacao_admin_detalhes',
                        idRec: idRec || null
                    }
                })
                .eq('assinatura_id', assinaturaId)
                .eq('status', 'pendente')
        }

        if (idRec) {
            await supabase.rpc('atualizar_status_pagamento', {
                p_txid: idRec,
                p_status: 'pago',
                p_end_to_end_id: null,
                p_data_pagamento: dataPagamento,
                p_detalhes_pix: {
                    provider: 'efi',
                    origem: 'reconciliacao_admin_detalhes',
                    idRec
                }
            })
        }

        // Ajuste visual imediato caso o persist falhe por RLS
        return lista.map((p) => {
            const st = (p.status || '').toLowerCase()
            if (st !== 'pendente' && st !== 'pending') return p
            if ((p.metodo_pagamento || '').toLowerCase() !== 'pix') return p

            const matchTxid = idRec && (p.txid === idRec || String(p.txid || '').includes(idRec))
            const venc = extractDateOnly(p.data_vencimento)
            const hoje = new Date()
            const limite = new Date(hoje)
            limite.setDate(limite.getDate() + 2)
            const vencOk = !venc || new Date(`${venc}T00:00:00`) <= limite
            const matchValorAtiva = Number(p.valor) === Number(planoPreco) && vencOk

            if (matchTxid || matchValorAtiva) {
                return {
                    ...p,
                    status: 'pago',
                    data_pagamento: p.data_pagamento || dataPagamento
                }
            }
            return p
        })
    }

    const extractDateOnly = (dateString: string | null): string | null => {
        if (!dateString) return null
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString
        const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/)
        return dateMatch ? dateMatch[1] : null
    }

    const carregarPagamentos = async () => {
        setLoading(true)
        try {
            const { data } = await supabase.rpc('listar_pagamentos_usuario', {
                p_user_id: userId,
                p_limit: 100,
                p_offset: 0
            })

            let lista: Pagamento[] = data?.sucesso ? (data.pagamentos || []) : []
            lista = await marcarPagamentosComoPago(lista)

            const { data: data2 } = await supabase.rpc('listar_pagamentos_usuario', {
                p_user_id: userId,
                p_limit: 100,
                p_offset: 0
            })

            if (data2?.sucesso) {
                const listaPersistida: Pagamento[] = data2.pagamentos || []
                const reconciliada = await marcarPagamentosComoPago(listaPersistida)
                const aindaPendente = listaPersistida.some((p) => {
                    const st = (p.status || '').toLowerCase()
                    return st === 'pendente' || st === 'pending'
                })
                setPagamentos(aindaPendente ? reconciliada : listaPersistida)
            } else {
                setPagamentos(lista)
            }
        } catch (error) {
            console.error('Erro ao carregar pagamentos:', error)
        } finally {
            setLoading(false)
        }
    }

    const getStatusBadge = (statusPagamento: string) => {
        const badges = {
            pago: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, label: 'Pago' },
            received: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, label: 'Recebido' },
            confirmed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle, label: 'Confirmado' },
            pendente: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock, label: 'Pendente' },
            pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock, label: 'Pendente' },
            agendado: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock, label: 'Agendado' },
            expirado: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: AlertTriangle, label: 'Expirado' },
            overdue: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: AlertTriangle, label: 'Atrasado' },
            cancelado: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', icon: XCircle, label: 'Cancelado' },
            falha: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle, label: 'Falha' }
        }
        const st = statusPagamento.toLowerCase()
        const badge = badges[st as keyof typeof badges] || badges.pendente
        const Icon = badge.icon
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="w-3 h-3" />
                {badge.label}
            </span>
        )
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    const formatDate = (date: string | null) => {
        if (!date) return '-'
        const dateOnly = extractDateOnly(date)
        if (!dateOnly) return '-'
        const [year, month, day] = dateOnly.split('-')
        return `${day}/${month}/${year}`
    }

    // Lógica inteligente para data de pagamento
    const getEffectivePaymentDate = (pagamento: Pagamento) => {
        if (pagamento.data_pagamento) return formatDate(pagamento.data_pagamento)

        // Se status é pago/recebido/confirmado mas data_pagamento é nula
        if (['pago', 'received', 'confirmed', 'recebido'].includes(pagamento.status.toLowerCase())) {
            // Usa data de vencimento ou data de criação como fallback
            return formatDate(pagamento.data_vencimento || pagamento.created_at)
        }
        return '-'
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Detalhes da Assinatura</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{corretorNome}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Informações Gerais */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Informações Gerais</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Email:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{corretorEmail}</p>
                            </div>
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Plano:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{planoNome} - {formatCurrency(planoPreco)}/mês</p>
                            </div>
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Status:</span>
                                <p className={`font-medium capitalize ${status === 'ativa' ? 'text-green-600 dark:text-green-400' :
                                    status === 'trial' ? 'text-blue-600 dark:text-blue-400' :
                                        status === 'cancelada' ? 'text-red-600 dark:text-red-400' :
                                            'text-gray-900 dark:text-gray-100'
                                    }`}>
                                    {status}
                                </p>
                                {(
                                    String(observacoes || '').toLowerCase().includes('cancelado via app do banco') ||
                                    String(recorrencia?.detalhes_efi?.motivo_cancelamento || '').toLowerCase().includes('cancelado via app do banco') ||
                                    recorrencia?.detalhes_efi?.cancelamento_origem === 'app_banco' ||
                                    ['cancelada', 'rejeitada'].includes(String(recorrencia?.status || '').toLowerCase())
                                ) && (
                                    <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                                        Cancelado via app do banco
                                    </span>
                                )}
                            </div>
                            <div>
                                <span className="text-gray-600 dark:text-gray-400">Início:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(dataInicio)}</p>
                            </div>
                            {dataFim && (
                                <div>
                                    <span className="text-gray-600 dark:text-gray-400">Fim/Renovação:</span>
                                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(dataFim)}</p>
                                </div>
                            )}
                            {dataProximoPagamento && (
                                <div>
                                    <span className="text-gray-600 dark:text-gray-400">Próximo Pagamento:</span>
                                    <p className="font-medium text-blue-600 dark:text-blue-400">{formatDate(dataProximoPagamento)}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assinatura Asaas */}
                    {asaasAssinatura && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-3 border border-blue-200 dark:border-blue-800">
                            <h3 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                Assinatura Asaas
                            </h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-600 dark:text-gray-400">ID Externo:</span>
                                    <p className="font-mono text-xs text-gray-900 dark:text-gray-100">{asaasAssinatura.asaas_subscription_id}</p>
                                </div>
                                <div>
                                    <span className="text-gray-600 dark:text-gray-400">Status Asaas:</span>
                                    <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">{asaasAssinatura.status}</p>
                                </div>
                                <div>
                                    <span className="text-gray-600 dark:text-gray-400">Ciclo:</span>
                                    <p className="font-medium text-gray-900 dark:text-gray-100 uppercase">{
                                        asaasAssinatura.cycle === 'MONTHLY' ? 'MENSAL' : asaasAssinatura.cycle
                                    }</p>
                                </div>
                                <div>
                                    <span className="text-gray-600 dark:text-gray-400">Valor Asaas:</span>
                                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(asaasAssinatura.valor)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Histórico de Pagamentos */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Histórico de Pagamentos</h3>
                            <button
                                onClick={carregarPagamentos}
                                disabled={loading}
                                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm flex items-center gap-1 transition-colors"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                Atualizar
                            </button>
                        </div>

                        {loading ? (
                            <div className="text-center py-8">
                                <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin mx-auto" />
                            </div>
                        ) : pagamentos.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                                <p>Nenhum pagamento registrado no sistema.</p>
                                {status === 'ativa' && (
                                    <p className="text-xs text-gray-400 mt-1">Isso é normal se a assinatura foi ativada manualmente ou via importação.</p>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Data Venc.</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Valor</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Método</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Data Pag.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                        {pagamentos.map((pagamento) => (
                                            <tr key={pagamento.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                                    {formatDate(pagamento.data_vencimento)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                                                    {formatCurrency(pagamento.valor)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {getStatusBadge(pagamento.status)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap capitalize text-gray-900 dark:text-gray-100">
                                                    {pagamento.metodo_pagamento || '-'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                                    {getEffectivePaymentDate(pagamento)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 text-xs text-center text-gray-500">
                    ID do Sistema: {assinaturaId}
                </div>
            </div>
        </div>
    )
}
